import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../EditUser/AuthProvider";

function EditPermission() {
  const { userId } = useParams();
  const { user } = useAuth();

  const [pages, setPages] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [targetUserRole, setTargetUserRole] = useState("");

  // Get current user info from localStorage
  const localUserId = parseInt(localStorage.getItem("userId"), 10);
  const currentUserRole = localStorage.getItem("userRole");
  const urlUserId = parseInt(userId, 10);

  // Check if user is viewing their own account
  const isViewingOwnAccount = localUserId === urlUserId;
  
  // Check if current user is admin
  const isAdmin = currentUserRole === 'Admin';
  
  // Check if viewing own admin account
  const isViewingOwnAdmin = isViewingOwnAccount && isAdmin;

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const fetchPages = fetch("https://hms.backend.hiragroupcloud.com/api/page/pages").then((r) =>
      r.json()
    );
    const fetchPerms = fetch(
      `https://hms.backend.hiragroupcloud.com/api/page/user/${userId}/pages`
    ).then((r) => r.json());
    
    // Fetch target user's data to get their role
    const fetchUserData = fetch(
      `https://hms.backend.hiragroupcloud.com/api/auth/users/${userId}`
    ).then(r => r.json());

    Promise.all([fetchPages, fetchPerms, fetchUserData])
      .then(([pagesData, permsData, userData]) => {
        if (!mounted) return;
        setPages(Array.isArray(pagesData) ? pagesData : []);
        
        // Set target user's role
        if (userData && userData.user && userData.user.role) {
          setTargetUserRole(userData.user.role);
        }
        
        const permissionMap = Array.isArray(permsData)
          ? permsData.reduce((acc, perm) => {
              if (perm && perm.page && perm.page._id) {
                acc[perm.page._id] = !!perm.isVisible;
              }
              return acc;
            }, {})
          : {};
        setPermissions(permissionMap);
      })
      .catch((err) => {
        console.error("Error fetching pages or permissions:", err);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  const handlePermissionChange = (pageId, isVisible) => {
    // Don't allow if:
    // 1. Viewing own admin account
    // 2. Not an admin
    // 3. Target user is also admin (admins can't modify other admins)
    if (isViewingOwnAdmin || !isAdmin || targetUserRole === 'Admin') return;
    
    setPermissions((prev) => ({
      ...prev,
      [pageId]: isVisible,
    }));
  };

  const handleSubmit = () => {
    // Check if current user is admin
    if (!isAdmin) {
      alert("Only Admin users can modify permissions");
      return;
    }

    // Check if viewing own admin account
    if (isViewingOwnAdmin) {
      alert("You're viewing your own admin account — changes are not allowed here.");
      return;
    }
    
    // Check if target user is also admin
    if (targetUserRole === 'Admin') {
      alert("Cannot modify permissions of Admin users");
      return;
    }

    const updatedPermissions = Object.keys(permissions).map((pageId) => ({
      pageId,
      isVisible: !!permissions[pageId],
    }));

    fetch(`https://hms.backend.hiragroupcloud.com/api/page/user/${userId}/permissions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        permissions: updatedPermissions,
        requestingUserId: localUserId,
        requestingUserRole: currentUserRole
      }),
    })
      .then((res) => res.json())
      .then((data) => alert(data.message || "Permissions updated"))
      .catch((err) => console.error("Error updating permissions:", err));
  };

  if (loading) return <div>Loading...</div>;

  // Check if user has permission to edit permissions
  if (!isAdmin) {
    return <div>Access Denied: Only Admin users can edit permissions</div>;
  }

  // group pages by category
  const groupedPages = pages.reduce((acc, page) => {
    const cat = page.category || "Other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(page);
    return acc;
  }, {});

  // Determine if should disable
  const shouldDisable = isViewingOwnAdmin || targetUserRole === 'Admin';

  return (
    <>
      <h1 style={{ marginTop: "20px", fontSize: "28px" }}>
        Permissions
      </h1>

      <div
        style={{
          borderTop: "3px solid #004aad",
          backgroundColor: "#f1f1f1",
          padding: "22px",
          marginTop: "10px",
          borderRadius: "4px",
          maxWidth: "1380px",
        }}
      >
     

        {Object.keys(groupedPages).map((category) => (
          <div
            key={category}
            className="form-group"
            style={{ marginBottom: "18px" }}
          >
            <div style={{ fontWeight: "700", marginBottom: "8px" }}>
              {category}
            </div>

            <ul style={{ listStyleType: "none", paddingLeft: "8px", margin: 0 }}>
              {groupedPages[category].map((page) => {
                const checked = shouldDisable
                  ? true
                  : !!permissions[page._id];

                return (
                  <li
                    key={page._id}
                    style={{
                      margin: "6px 0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={shouldDisable}
                      onChange={(e) =>
                        handlePermissionChange(page._id, e.target.checked)
                      }
                      style={{ marginRight: "10px" }}
                    />
                    <span>{page.name}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <button
          onClick={handleSubmit}
          disabled={shouldDisable}
          style={{
            marginTop: "18px",
            padding: "10px 20px",
            backgroundColor: shouldDisable ? "#7f9bd6" : "#004aad",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: shouldDisable ? "not-allowed" : "pointer",
          }}
        >
          {shouldDisable ? "Permissions Locked" : "Save Permissions"}
        </button>
      </div>
    </>
  );
}

export default EditPermission;